/**
 * Foundry Rooms: IPM's mothership rooms (GDD §6), bought and levelled with
 * Tower Essence — the Fold's payout, as IPM's Credits come from selling a
 * galaxy. Permanent.
 *
 * Mirrors the IPM wiki's Rooms page:
 *  - rooms are offered two at a time, in this order;
 *  - the Nth room you build costs ROOM_BUILD_COSTS[N] whichever you pick;
 *  - multiplier rooms run to level 60, reduction rooms to 11 (−50%), the
 *    Archive (Backup Generator) to 44 (24h idle).
 * Level prices past the first are not on the wiki: each level costs the
 * room's minimum cost × 1.235^level, which reproduces the ~4M credits the
 * guides quote to max Engineering. Provisional (GDD §12). Robotics is IPM's
 * rover room; the Contracts Office it once stood in for is off with Contracts.
 */

export type RoomEffect =
  | 'mine' | 'ship' | 'cargo' | 'smelt' | 'craft'
  | 'upgradeCost' | 'researchCost' | 'colonyCost' | 'foldTe' | 'contractVs'
  | 'offline' | 'smeltInputs' | 'craftInputs' | 'refinedValue' | 'prospector'
  | 'market' | 'colonyBonus' | 'unlockCost' | 'roverTime';

export interface RoomDef {
  readonly id: string;
  readonly name: string;
  /** IPM's room this one is. */
  readonly ipm: string;
  readonly glyph: string;
  readonly color: string;
  readonly effect: RoomEffect;
  readonly shape: 'mult' | 'cut' | 'offline';
  readonly start: number;
  readonly perLevel: number;
  readonly maxLevel: number;
  /** IPM's "Min Cost": the price if this were the Nth room bought in offer order. Seeds level prices. */
  readonly minCost: number;
  readonly text: string;
}

const mult = (id: string, name: string, ipm: string, glyph: string, color: string, effect: RoomEffect, start: number, perLevel: number, minCost: number, text: string): RoomDef =>
  ({ id, name, ipm, glyph, color, effect, shape: 'mult', start, perLevel, maxLevel: 60, minCost, text });
const cut = (id: string, name: string, ipm: string, glyph: string, color: string, effect: RoomEffect, minCost: number, text: string): RoomDef =>
  ({ id, name, ipm, glyph, color, effect, shape: 'cut', start: 0.1, perLevel: 0.04, maxLevel: 11, minCost, text });

/** In IPM's offer order. */
export const ROOMS: readonly RoomDef[] = [
  mult('engineering', 'Engineering', 'Engineering', '⛏', '#f5c542', 'mine', 1.25, 0.15, 3, 'Mining speed on every planet'),
  mult('forge', 'Forge', 'Forge', '♨', '#ff7a3d', 'smelt', 1.2, 0.1, 3, 'Smelter speed'),
  mult('hangar', 'Hangar', 'Aeronautical', '➤', '#4dd9f5', 'ship', 1.5, 0.25, 6, 'Ship speed on every planet'),
  cut('observatory', 'Observatory', 'Astronomy', '◎', '#c7a7ff', 'upgradeCost', 12, 'Planet upgrade costs'),
  mult('packaging', 'Packaging Bay', 'Packaging', '▣', '#7fe6ff', 'cargo', 1.5, 0.25, 21, 'Ship cargo on every planet'),
  mult('workshop', 'Workshop', 'Workshop', '⚒', '#9b7bff', 'craft', 1.2, 0.1, 35, 'Crafter speed'),
  cut('lab', 'Laboratory', 'Laboratory', '⚗', '#9b7bff', 'researchCost', 56, 'Project costs'),
  cut('robotics', 'Robotics', 'Robotics', '⚙', '#4dd9f5', 'roverTime', 87, 'Rover mission time (level 4: 48h missions)'),
  mult('lounge', 'Lounge', 'Lounge', '◇', '#f5c542', 'foldTe', 1.15, 0.05, 133, 'TE from every Fold'),
  { id: 'archive', name: 'Archive', ipm: 'Backup Generator', glyph: '⧗', color: '#ff7aab', effect: 'offline', shape: 'offline', start: 2, perLevel: 0.5, maxLevel: 44, minCost: 200, text: 'Offline time (hours)' },
  cut('terrarium', 'Terrarium', 'Terrarium', '❦', '#4ee08a', 'colonyCost', 298, 'Items needed to colonize'),
  cut('underforge', 'Underforge', 'Underforge', '♆', '#ff7a3d', 'smeltInputs', 439, 'Ingredients per bar'),
  cut('dorms', 'Dorms', 'Dorm', '⌂', '#9b7bff', 'craftInputs', 642, 'Ingredients per item'),
  mult('sales', 'Sales', 'Sales', '◈', '#4ee08a', 'refinedValue', 1.15, 0.05, 1351, 'Value of bars and items'),
  mult('classroom', 'Classroom', 'Classroom', '✎', '#4dd9f5', 'prospector', 1.15, 0.05, 1946, 'Prospector bonuses'),
  mult('marketing', 'Marketing', 'Marketing', '↗', '#f5c542', 'market', 1.3, 0.1, 2932, 'Positive market swings'),
  mult('relations', 'Planet Relations', 'Planet Relations', '⚑', '#4ee08a', 'colonyBonus', 1.25, 0.1, 4402, 'Colony bonuses'),
  cut('cartography', 'Cartography', 'Belt Studies', '⌖', '#c7a7ff', 'unlockCost', 6586, 'Planet unlock costs'),
];

/** TE to build your Nth room (0-based), whichever room it is — IPM's room price ladder. */
export const ROOM_BUILD_COSTS: readonly number[] = [3, 6, 12, 21, 35, 56, 87, 133, 200, 298, 439, 642, 934, 1351, 1946, 2932, 4402, 6586];

/** How many unbuilt rooms are offered at once. */
export const ROOM_OFFERS = 2;

export const ROOM_LEVEL_GROWTH = 1.235;

export function roomDef(id: string): RoomDef {
  const def = ROOMS.find((r) => r.id === id);
  if (!def) throw new Error(`Unknown room: ${id}`);
  return def;
}

/** TE to raise a built room from `level` to `level + 1`. Null at max. */
export function roomLevelCost(def: RoomDef, level: number): number | null {
  if (level >= def.maxLevel) return null;
  return Math.ceil(def.minCost * Math.pow(ROOM_LEVEL_GROWTH, level));
}

/**
 * A room's value at a level. Multiplier rooms return ×value (1 when unbuilt),
 * reduction rooms the fraction cut (0 when unbuilt), the Archive hours.
 */
export function roomValue(def: RoomDef, level: number): number {
  if (def.shape === 'offline') return def.start + def.perLevel * level;
  if (level <= 0) return def.shape === 'mult' ? 1 : 0;
  return def.start + def.perLevel * (level - 1);
}
